import { useState, useEffect, useCallback, useRef } from "react";
import { type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useApi } from "./useApi";
import { useMode } from "@/providers/ModeProvider";

interface ColumnConfigApiResponse {
  screenName: string;
  columnId: string;
  isVisible: boolean;
  order: number;
}

function mergeApiWithDefaults(
  apiConfigs: ColumnConfigApiResponse[],
  defaults: ColumnDefinition[]
): ColumnDefinition[] {
  const apiMap = new Map(apiConfigs.map((x) => [x.columnId, x]));
  const defaultIds = new Set(defaults.map((col) => col.id));

  // Only keep API configs that exist in defaults
  const filteredApiConfigs = apiConfigs.filter((apiCol) => defaultIds.has(apiCol.columnId));
  const filteredApiMap = new Map(filteredApiConfigs.map((x) => [x.columnId, x]));

  return defaults
    .map((defaultCol) => {
      const apiCol = filteredApiMap.get(defaultCol.id);
      if (apiCol) {
        return {
          ...defaultCol,
          visible: apiCol.isVisible,
          order: apiCol.order,
        };
      }
      return defaultCol;
    })
    .sort((a, b) => a.order - b.order);
}

function mergeWithDefaults(
  stored: ColumnDefinition[],
  defaults: ColumnDefinition[]
): ColumnDefinition[] {
  const defaultIds = new Set(defaults.map((col) => col.id));
  
  // Only keep stored columns that exist in defaults
  const filteredStored = stored.filter((col) => defaultIds.has(col.id));
  const storedMap = new Map(filteredStored.map((col) => [col.id, col]));

  return defaults
    .map((defaultCol) => {
      const storedCol = storedMap.get(defaultCol.id);
      if (storedCol) {
        return {
          ...defaultCol,
          visible: storedCol.visible,
          order: storedCol.order,
        };
      }
      return defaultCol;
    })
    .sort((a, b) => a.order - b.order);
}

function loadFromLocalStorage(
  storageKey: string,
  defaults: ColumnDefinition[]
): ColumnDefinition[] {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored) as ColumnDefinition[];
      return mergeWithDefaults(parsed, defaults);
    }
  } catch {
    console.warn(`Failed to load column preferences from localStorage for key: ${storageKey}`);
  }
  return defaults;
}

export function useScreenColumnPreferences(
  screenName: string,
  defaultColumns: ColumnDefinition[]
) {
  const storageKey = `${screenName}-column-preferences`;
  const [columns, setColumns] = useState<ColumnDefinition[]>(() =>
    loadFromLocalStorage(storageKey, defaultColumns)
  );
  const [isLoading, setIsLoading] = useState(true);
  const { apiGet, apiBaseUrl, getHeaders } = useApi();
  const { isDemo } = useMode();
  const apiLoadedRef = useRef(false);

  useEffect(() => {
    // In demo mode, skip API call and only use localStorage
    if (isDemo) {
      setIsLoading(false);
      return;
    }

    if (apiLoadedRef.current) return;
    let cancelled = false;

    async function loadFromApi() {
      try {
        const response = await apiGet<ColumnConfigApiResponse[]>(
          "/api/v1/columnConfig"
        );
        if (cancelled) return;

        const screenConfigs = response.filter(
          (x) => x.screenName === screenName
        );
        if (screenConfigs.length > 0) {
          const merged = mergeApiWithDefaults(screenConfigs, defaultColumns);
          setColumns(merged);
          localStorage.setItem(storageKey, JSON.stringify(merged));
        }
        apiLoadedRef.current = true;
      } catch {
        // Will retry when apiGet changes
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadFromApi();
    return () => {
      cancelled = true;
    };
  }, [apiGet, screenName, defaultColumns, storageKey, isDemo]);

  const saveColumnsToApi = useCallback(
    async (columnsToSave: ColumnDefinition[]) => {
      // In demo mode, do not call the API
      if (isDemo) return;

      const headers = await getHeaders();
      const response = await fetch(`${apiBaseUrl}/api/v1/columnConfig`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          screenName,
          columns: columnsToSave.map((col, index) => ({
            columnId: col.id,
            isVisible: col.visible,
            order: index,
          })),
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to save column config: ${response.status}`);
      }
    },
    [apiBaseUrl, getHeaders, screenName, isDemo]
  );

  const savePreferences = useCallback(
    async (newColumns: ColumnDefinition[]) => {
      setColumns(newColumns);
      try {
        localStorage.setItem(storageKey, JSON.stringify(newColumns));
      } catch {
        console.warn("Failed to cache column preferences to localStorage");
      }
      try {
        await saveColumnsToApi(newColumns);
      } catch (error) {
        console.error("Failed to save column preferences to API:", error);
      }
    },
    [saveColumnsToApi, storageKey]
  );

  const resetToDefaults = useCallback(async () => {
    setColumns(defaultColumns);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      console.warn("Failed to clear column preferences from localStorage");
    }
    try {
      await saveColumnsToApi(defaultColumns);
    } catch (error) {
      console.error("Failed to reset column preferences via API:", error);
    }
  }, [saveColumnsToApi, storageKey, defaultColumns]);

  const visibleColumns = columns
    .filter((col) => col.visible)
    .sort((a, b) => a.order - b.order);

  return {
    columns,
    visibleColumns,
    isLoading,
    savePreferences,
    resetToDefaults,
  };
}
